import { apiClient } from './api.service';

export interface SystemPermission {
  id: number;
  code: string;
  name: string;
  module: string;
  menu?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeSystemPermission {
  id: number;
  code: string;
  name: string;
  module: string;
  description?: string;
  grantedAt: string;
  grantedBy?: number;
}

export interface EmployeeLocationAccess {
  locationId: number;
  locationName: string;
  accessLevel: 'read' | 'write' | 'admin';
  grantedAt: string;
  grantedBy?: number;
  expiresAt?: string;
}

export interface EmployeePermissions {
  employeeId: number;
  systemPermissions: EmployeeSystemPermission[];
  locationPermissions: EmployeeLocationAccess[];
  totalSystemPermissions: number;
  totalLocationAccess: number;
}

export interface PermissionsByModule {
  [module: string]: SystemPermission[];
}

export interface PermissionsByMenu {
  [menu: string]: SystemPermission[];
}

export interface SystemPermissionsResponse {
  permissions: SystemPermission[];
  permissionsByModule: PermissionsByModule;
  permissionsByMenu: PermissionsByMenu;
  modules: string[];
  menus: string[];
}

export interface GrantPermissionRequest {
  permissionIds?: number[];
  locationAccess?: {
    locationId: number;
    accessLevel: 'read' | 'write' | 'admin';
    expiresAt?: string;
  }[];
}

export interface RevokePermissionRequest {
  permissionIds?: number[];
  locationIds?: number[];
}

export interface PermissionCheckRequest {
  permissionCodes?: string[];
  locationId?: number;
  requiredAccessLevel?: 'read' | 'write' | 'admin';
}

export interface PermissionCheckResult {
  hasAllPermissions: boolean;
  permissionChecks: { [code: string]: boolean };
  locationAccess?: {
    locationId: number;
    hasAccess: boolean;
    requiredLevel: string;
  };
}

export interface LocationEmployeesResponse {
  locationId: number;
  employees: {
    employeeId: number;
    employee: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      role: string;
    };
    accessLevel: string;
    grantedAt: string;
    grantedBy?: number;
    expiresAt?: string;
    isExpired: boolean;
  }[];
  totalEmployees: number;
  activeAccess: number;
}

class PermissionsService {
  private baseUrl = '/permissions';

  /**
   * Get all system permissions
   */
  async getSystemPermissions(module?: string): Promise<SystemPermissionsResponse> {
    const params = module && module !== 'all' ? { module } : {};
    const response = await apiClient.get(`${this.baseUrl}/system`, { params });
    return response.data;
  }

  /**
   * Get employee permissions
   */
  async getEmployeePermissions(employeeId: number): Promise<EmployeePermissions> {
    const response = await apiClient.get(`${this.baseUrl}/employee/${employeeId}`);
    return response.data;
  }

  /**
   * Grant permissions to employee
   */
  async grantEmployeePermissions(
    employeeId: number,
    request: GrantPermissionRequest
  ): Promise<any> {
    const response = await apiClient.post(
      `${this.baseUrl}/employee/${employeeId}/grant`,
      request
    );
    return response.data;
  }

  /**
   * Revoke permissions from employee
   */
  async revokeEmployeePermissions(
    employeeId: number,
    request: RevokePermissionRequest
  ): Promise<any> {
    const response = await apiClient.delete(
      `${this.baseUrl}/employee/${employeeId}/revoke`,
      { data: request }
    );
    return response.data;
  }

  /**
   * Check if employee has specific permissions
   */
  async checkEmployeePermissions(
    employeeId: number,
    request: PermissionCheckRequest
  ): Promise<PermissionCheckResult> {
    const response = await apiClient.post(
      `${this.baseUrl}/employee/${employeeId}/check`,
      request
    );
    return response.data;
  }

  /**
   * Get employees with access to a location
   */
  async getLocationEmployees(locationId: number): Promise<LocationEmployeesResponse> {
    const response = await apiClient.get(`${this.baseUrl}/location/${locationId}/employees`);
    return response.data;
  }

  /**
   * Helper: Check if employee has permission by code
   */
  async hasPermission(employeeId: number, permissionCode: string): Promise<boolean> {
    const result = await this.checkEmployeePermissions(employeeId, {
      permissionCodes: [permissionCode]
    });
    return result.permissionChecks[permissionCode] || false;
  }

  /**
   * Helper: Check if employee has location access
   */
  async hasLocationAccess(
    employeeId: number,
    locationId: number,
    requiredLevel: 'read' | 'write' | 'admin' = 'read'
  ): Promise<boolean> {
    const result = await this.checkEmployeePermissions(employeeId, {
      locationId,
      requiredAccessLevel: requiredLevel
    });
    return result.locationAccess?.hasAccess || false;
  }

  /**
   * Helper: Get permissions grouped by module for UI display
   */
  async getPermissionsForUI(): Promise<{
    modules: string[];
    permissionsByModule: PermissionsByModule;
  }> {
    const data = await this.getSystemPermissions();
    return {
      modules: data.modules,
      permissionsByModule: data.permissionsByModule
    };
  }

  /**
   * Helper: Get permissions grouped by menu for UI display
   */
  async getPermissionsForUIByMenu(): Promise<{
    menus: string[];
    permissionsByMenu: PermissionsByMenu;
  }> {
    const data = await this.getSystemPermissions();
    return {
      menus: data.menus || [],
      permissionsByMenu: data.permissionsByMenu || {}
    };
  }
}

export const permissionsService = new PermissionsService();